import { dssChainData, dssConfigFor } from '@1delta/data-sdk'
import { UsddJugAbi, UsddSpotAbi, UsddVatAbi } from '@1delta/abis'
import { erc20Abi, isAddress, stringToHex } from 'viem'
import { multicallRetryUniversal } from '@1delta/providers'
import type { DssMarketRaw, DssMarketsRaw } from './types'

// Up to 4 reads per ilk; the gem-join balance is CONDITIONAL (a roster row
// without a usable join address skips it), so the slicer works off the
// per-market index recorded while building the calls, not a fixed stride.
const CORE_READS_PER_ILK = 3

// PER-CALL ABIs — Vat/Jug/Spot all declare `ilks(bytes32)` with DIFFERENT
// output tuples, and viem decodes by function NAME (it has no target-address
// context), so a merged ABI silently decodes every `ilks` result against the
// first match (the Vat's 5-tuple) and the Jug/Spot reads come back null.
// The multicall runner accepts an ABI **array** (one ABI per call) exactly
// for this case. Order matches the per-ilk read block below.
const DSS_CORE_ABI_PER_READ = [UsddVatAbi, UsddJugAbi, UsddSpotAbi]

/** Ilk string → bytes32 (`'WBTC-A'` → right-padded hex). */
export const dssIlkBytes32 = (ilk: string): `0x${string}` =>
  stringToHex(ilk, { size: 32 })

/**
 * Fetch all market data of ONE dss (MakerDAO-shaped) deployment — FULLY
 * ON-CHAIN via one retrying multicall. Serves every dss brand (`SKY`, `USDD`,
 * any future fork): the deployment addresses and the ilk roster come from
 * lender-metadata through the brand-agnostic `dssConfigFor`/`dssChainData`
 * resolvers, and this fetch reads the LIVE Vat/Jug/Spot params per ilk plus
 * the gem-join balance (total custodied collateral — the Vat keeps no per-ilk
 * ink total).
 *
 * An empty roster (the standing USDD state on both EVM chains — `cdpi() = 0`,
 * no ilk filed, see USDD.md) returns zero markets without issuing a
 * multicall, so the code path stays live and metadata alone activates a
 * deployment.
 */
export async function fetchDssMarkets(
  lender: string,
  chainId: string,
): Promise<DssMarketsRaw> {
  const config = dssConfigFor(lender, chainId)
  const chainData = dssChainData(lender, chainId)
  const markets = chainData?.markets ?? []
  if (!config || markets.length === 0) {
    return { lender, config, chainData, markets: [] }
  }

  const calls: { address: string; name: string; params: any[] }[] = []
  const abi: any[] = []
  // Index of each market's gem-join balance read, or -1 when skipped.
  const joinReadIndex: number[] = []

  for (const m of markets) {
    const ilk32 = dssIlkBytes32(m.ilk)
    calls.push(
      { address: config.vat, name: 'ilks', params: [ilk32] },
      { address: config.jug, name: 'ilks', params: [ilk32] },
      { address: config.spot, name: 'ilks', params: [ilk32] },
    )
    abi.push(...DSS_CORE_ABI_PER_READ)
    // A malformed/absent gem join must not poison the whole batch: viem
    // throws while ENCODING an invalid address, which would take out every
    // other market's reads too. Skip the leg and report `joinBalance: null`.
    if (isAddress(m.gemJoin ?? '', { strict: false })) {
      joinReadIndex.push(calls.length)
      calls.push({
        address: m.collToken,
        name: 'balanceOf',
        params: [m.gemJoin],
      })
      abi.push(erc20Abi)
    } else {
      joinReadIndex.push(-1)
    }
  }

  let results: any[] = []
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: abi as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return { lender, config, chainData, markets: [] }
  }

  const big = (v: any): bigint | null => {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number') return BigInt(v)
    return null
  }
  // Tuple field access — decoded structs may arrive named or positional.
  const field = (res: any, name: string, idx: number): bigint | null =>
    big(res?.[name] ?? res?.[idx])

  let cursor = 0
  const out: DssMarketRaw[] = markets.map((market, i) => {
    const base = cursor
    cursor += CORE_READS_PER_ILK + (joinReadIndex[i] >= 0 ? 1 : 0)
    const vatIlk = results[base]
    const jugIlk = results[base + 1]
    const spotIlk = results[base + 2]
    return {
      market,
      Art: field(vatIlk, 'Art', 0),
      rate: field(vatIlk, 'rate', 1),
      spot: field(vatIlk, 'spot', 2),
      line: field(vatIlk, 'line', 3),
      dust: field(vatIlk, 'dust', 4),
      duty: field(jugIlk, 'duty', 0),
      mat: field(spotIlk, 'mat', 1),
      joinBalance:
        joinReadIndex[i] >= 0 ? big(results[joinReadIndex[i]]) : null,
    }
  })

  return { lender, config, chainData, markets: out }
}

/** @deprecated brand-specific alias — use `fetchDssMarkets`. */
export const fetchUsddMarkets = fetchDssMarkets
/** @deprecated brand-specific alias — use `dssIlkBytes32`. */
export const usddIlkBytes32 = dssIlkBytes32
